import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Library, RefreshCw, Trash2, Search, Music, Drum, Waves, Piano, Download, ShieldCheck, Loader2, Play } from "lucide-react";

const ROLE_ICON = { kick: Drum, snare: Drum, hat: Drum, clap: Drum, perc: Drum, "808": Waves, bass: Waves, melodic: Piano, vocal: Music, fx: Waves };
const ROLE_COLOR = { kick: "text-amber", snare: "text-amber", hat: "text-amber", "808": "text-cyan", bass: "text-cyan", melodic: "text-violet", vocal: "text-emerald-400", fx: "text-slate-300" };

export const SoundLibrary = () => {
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState(null);
  const [browsing, setBrowsing] = useState(false);

  const loadSummary = useCallback(() => api.get("/library/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const [catalog, setCatalog] = useState([]);
  const [importing, setImporting] = useState(null);
  useEffect(() => {
    loadSummary();
    api.get("/packs/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [loadSummary]);

  const importPack = async (pack) => {
    setImporting(pack.id);
    try {
      const r = await api.post("/packs/import", { pack_id: pack.id });
      toast.success(`Imported ${r.data.imported} vetted sounds`, {
        description: `${r.data.license} · scan: ${r.data.scan_mode}. Your DAW will pull them next agent sync.`,
      });
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(null); }
  };

  const browse = async (r = role, query = q) => {
    setBrowsing(true);
    try {
      const params = {};
      if (r) params.role = r;
      if (query) params.q = query;
      const res = await api.get("/library/assets", { params });
      setAssets(res.data);
    } catch (e) { toast.error("Could not load sounds"); }
    finally { setBrowsing(false); }
  };

  const previewAsset = async (id) => {
    try {
      const r = await api.get(`/library/asset/${id}/preview`, { responseType: "blob" });
      new Audio(URL.createObjectURL(r.data)).play();
    } catch (e) { toast.message("Preview not available", { description: "This sound is on your machine — audition it in your DAW." }); }
  };

  const clearLib = async () => {
    if (!window.confirm("Remove all indexed sounds? Your files are untouched; this only clears the index.")) return;
    await api.delete("/library");
    setAssets([]); loadSummary();
    toast.success("Library index cleared");
  };

  const total = summary?.total || 0;
  const roles = Object.entries(summary?.by_role || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="card-surface rounded-2xl p-6" data-testid="sound-library">
      <div className="flex items-center gap-2 mb-4">
        <Library className="w-5 h-5 text-gold" />
        <h2 className="font-display font-semibold text-lg text-slate-50">Your Sound Library</h2>
        <span className="ml-auto flex items-center gap-2">
          <Button data-testid="library-refresh" onClick={loadSummary} className="rounded-full bg-white/10 hover:bg-white/15 h-8 px-3 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          {total > 0 && <Button data-testid="library-clear" onClick={clearLib} className="rounded-full bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300 h-8 px-3 text-xs"><Trash2 className="w-3.5 h-3.5" /></Button>}
        </span>
      </div>

      {total === 0 ? (
        <div className="text-sm text-slate-400 bg-black/30 rounded-xl p-5" data-testid="library-empty">
          No sounds indexed yet. Pair a DAW above and run the <span className="text-gold">Aura Agent</span> — it scans your sample folders
          (filenames, tags + light audio analysis, <span className="text-slate-200">no audio leaves your machine</span>) so the engine can load
          <span className="text-gold"> your real packs</span> onto each track when you build.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-black/30 rounded-xl p-3"><p className="label-mono text-slate-500 text-[10px]">Total sounds</p><p className="font-display font-bold text-2xl text-gold">{total.toLocaleString()}</p></div>
            <div className="bg-black/30 rounded-xl p-3"><p className="label-mono text-slate-500 text-[10px]">Analyzed</p><p className="font-display font-bold text-2xl text-cyan">{summary.analyzed}</p></div>
            <div className="bg-black/30 rounded-xl p-3"><p className="label-mono text-slate-500 text-[10px]">Loops</p><p className="font-display font-bold text-2xl text-violet">{summary.loops}</p></div>
            <div className="bg-black/30 rounded-xl p-3"><p className="label-mono text-slate-500 text-[10px]">Instruments</p><p className="font-display font-bold text-2xl text-amber">{summary.instruments}</p></div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="library-role-chips">
            <button onClick={() => { setRole(null); browse(null, q); }} className={`text-xs px-2.5 py-1 rounded-full border ${!role ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>All</button>
            {roles.map(([r, n]) => (
              <button key={r} data-testid={`library-role-${r}`} onClick={() => { setRole(r); browse(r, q); }} className={`text-xs px-2.5 py-1 rounded-full border ${role === r ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>{r} <span className="text-slate-600">{n}</span></button>
            ))}
          </div>

          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && browse()} placeholder="Search your sounds…" className="pl-9 bg-white/5 border-white/10 h-9" />
            </div>
            <Button data-testid="library-browse" onClick={() => browse()} disabled={browsing} className="rounded-full btn-gold h-9 px-4 text-sm font-semibold">Browse</Button>
          </div>

          {assets.length > 0 && (
            <div className="max-h-72 overflow-y-auto rounded-xl border border-white/5 divide-y divide-white/5" data-testid="library-list">
              {assets.map((a) => {
                const I = ROLE_ICON[a.role] || Music;
                return (
                  <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-white/[0.03]">
                    <I className={`w-4 h-4 shrink-0 ${ROLE_COLOR[a.role] || "text-slate-400"}`} />
                    <div className="min-w-0 flex-1"><p className="text-slate-200 truncate">{a.name}</p><p className="text-[10px] text-slate-600 truncate font-mono-a">{a.folder}</p></div>
                    <span className="text-[10px] text-slate-500 shrink-0">{a.role}{a.kind === "loop" ? " · loop" : a.kind === "instrument" ? " · instr" : ""}{a.bpm ? ` · ${a.bpm}bpm` : ""}{a.key ? ` · ${a.key}` : ""}</span>
                    {a.source === "downloaded" && <button data-testid={`library-preview-${a.id}`} onClick={() => previewAsset(a.id)} title="Preview" className="p-1 rounded-md text-slate-400 hover:text-cyan hover:bg-white/5 shrink-0"><Play className="w-3.5 h-3.5" /></button>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Free packs — curated, license-clear, sandboxed + virus-scanned */}
      <div className="mt-6 pt-5 border-t border-white/5" data-testid="free-packs">
        <div className="flex items-center gap-2 mb-1">
          <Download className="w-4 h-4 text-cyan" />
          <h3 className="font-display font-semibold text-slate-100">Free packs</h3>
          <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full ml-1"><ShieldCheck className="w-3 h-3" /> sandboxed + virus-scanned</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Curated royalty-free / CC0 only — safe to use in releases. Downloaded server-side, audio-only, scanned, then your agent pulls the clean files to your machine.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {catalog.length === 0 && <p className="text-slate-600 text-sm">No packs available.</p>}
          {catalog.map((p) => (
            <div key={p.id} data-testid={`pack-${p.id}`} className="bg-black/30 rounded-xl p-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-slate-100 text-sm font-medium truncate">{p.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{p.provider} · {(p.genre_tags || []).join(", ")}</p>
                <p className="text-[10px] text-emerald-400/80 mt-0.5 truncate">{p.license}</p>
              </div>
              <Button data-testid={`pack-import-${p.id}`} onClick={() => importPack(p)} disabled={importing === p.id} className="rounded-full btn-gold h-8 px-3 text-xs font-semibold shrink-0">
                {importing === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Import</>}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
